/**
 * GeniusEngine 2.0 — Behavioral Telemetry System
 *
 * Replaces regex-only pattern matching with multi-dimensional
 * behavioral profiling. Tracks:
 * - Choice patterns (divergence index)
 * - Terminal input analysis (entropy, lexical diversity)
 * - Timing patterns (deliberation score)
 * - Retry/adaptation behavior (grit index)
 * - Exploration depth (novelty score)
 *
 * All data stored anonymously. Zero PII.
 * localStorage for MVP, Supabase integration in Phase 4.
 */

const STORAGE_KEY = 'gof_telemetry_v2';
const PROFILE_KEY = 'gof_player_profile';

class GeniusEngine {
  constructor(missionId) {
    this.missionId = missionId;
    this.events = [];
    this.flags = new Set();
    this.sessionStart = Date.now();
    this._lastEventTime = Date.now();

    // Behavioral metrics (computed in real-time)
    this.metrics = {
      choicePatterns: [],
      terminalInputs: [],
      timingDeltas: [],
      explorationDepth: 0,
      uniqueTerminalCommands: new Set(),
      totalInteractions: 0,
    };

    // Load cross-session profile
    this._profile = this._loadProfile();
  }

  // --- Event Tracking ---

  trackChoice(round, choice) {
    const now = Date.now();
    const deliberationMs = now - this._lastEventTime;
    this._lastEventTime = now;

    this.events.push({
      type: 'choice',
      round,
      choiceId: choice.id,
      choiceType: choice.type,
      trustDelta: choice.trustDelta,
      deliberationMs,
      timestamp: now - this.sessionStart,
    });

    this.metrics.choicePatterns.push(choice.type);
    this.metrics.timingDeltas.push(deliberationMs);
    this.metrics.totalInteractions++;
  }

  trackTerminalInput(input) {
    const now = Date.now();
    const deliberationMs = now - this._lastEventTime;
    this._lastEventTime = now;

    const analysis = this._analyzeInput(input);

    this.events.push({
      type: 'terminal_input',
      input: input.substring(0, 500),
      deliberationMs,
      timestamp: now - this.sessionStart,
      analysis,
    });

    this.metrics.terminalInputs.push({ input, analysis, deliberationMs });
    this.metrics.uniqueTerminalCommands.add(input.toLowerCase().trim());
    this.metrics.totalInteractions++;
  }

  trackSimulation(panelId, action, value) {
    const now = Date.now();
    this.events.push({
      type: 'simulation',
      panelId,
      action,
      value,
      timestamp: now - this.sessionStart,
    });
    this.metrics.totalInteractions++;
  }

  trackCompletion(outcome, trust) {
    this.events.push({
      type: 'mission_complete',
      outcome: outcome.title,
      outcomeType: outcome.type,
      trust,
      totalTime: Date.now() - this.sessionStart,
      geniusFlags: [...this.flags],
    });
  }

  // --- Behavioral Analysis ---

  /**
   * Evaluate terminal input for genius indicators.
   * Uses semantic analysis beyond pure regex matching.
   */
  evaluateTerminalInput(input, missionTrigger) {
    const analysis = this._analyzeInput(input);
    const result = { isGenius: false, flags: [], score: 0, analysis };

    // Level 1: Pattern match (basic — kept for backwards compat)
    if (missionTrigger.patterns && missionTrigger.patterns.some(p => p.test(input))) {
      result.isGenius = true;
      result.flags.push(missionTrigger.flag || 'DIVERGENT_THINKER');
      result.score += 50;
    }

    // Level 2: Semantic indicators
    if (analysis.entropy > 3.5 && analysis.lexicalDiversity > 0.7) {
      result.score += 20;
    }
    if (analysis.hasMetaAwareness) {
      result.score += 30;
      if (!result.flags.includes('META_COGNITION')) {
        result.flags.push('META_COGNITION');
      }
      result.isGenius = true;
    }
    if (analysis.hasSystemsThinking) {
      result.score += 25;
      if (!result.flags.includes('SYSTEMS_THINKER')) {
        result.flags.push('SYSTEMS_THINKER');
      }
      result.isGenius = true;
    }
    if (analysis.hasCreativeApproach) {
      result.score += 20;
    }

    if (result.isGenius) {
      result.flags.forEach(f => this.flags.add(f));
    }

    return result;
  }

  getProfile() {
    const choices = this.events.filter(e => e.type === 'choice');
    const terminals = this.events.filter(e => e.type === 'terminal_input');
    const sims = this.events.filter(e => e.type === 'simulation');

    return {
      missionId: this.missionId,
      sessionDuration: Date.now() - this.sessionStart,
      totalChoices: choices.length,
      choiceDistribution: this._getChoiceDistribution(choices),
      divergenceIndex: this._calcDivergenceIndex(choices),
      terminalAttempts: terminals.length,
      avgTerminalEntropy: this._avgEntropy(terminals),
      avgLexicalDiversity: this._avgLexicalDiversity(terminals),
      deliberationScore: this._calcDeliberationScore(),
      avgDeliberation: this._avgDeliberation(),
      explorationScore: this._calcExplorationScore(choices, terminals, sims),
      uniqueCommands: this.metrics.uniqueTerminalCommands.size,
      geniusFlags: [...this.flags],
      gritIndex: this._calcGritIndex(),
    };
  }

  // --- Internal Analysis ---

  _analyzeInput(input) {
    const words = input.toLowerCase().split(/\s+/).filter(w => w.length > 0);
    const chars = input.split('');

    return {
      length: input.length,
      wordCount: words.length,
      entropy: this._shannonEntropy(chars),
      lexicalDiversity: this._lexicalDiversity(words),
      hasMetaAwareness: this._detectMetaAwareness(input),
      hasSystemsThinking: this._detectSystemsThinking(input),
      hasCreativeApproach: this._detectCreativity(input),
      structuralComplexity: this._structuralComplexity(input),
    };
  }

  _shannonEntropy(chars) {
    if (chars.length === 0) return 0;
    const freq = {};
    for (const c of chars) freq[c] = (freq[c] || 0) + 1;
    let entropy = 0;
    const len = chars.length;
    for (const c in freq) {
      const p = freq[c] / len;
      if (p > 0) entropy -= p * Math.log2(p);
    }
    return entropy;
  }

  _lexicalDiversity(words) {
    if (words.length === 0) return 0;
    return new Set(words).size / words.length;
  }

  _detectMetaAwareness(input) {
    const lower = input.toLowerCase();
    const indicators = [
      'game', 'engine', 'algorithm', 'code', 'system', 'program',
      'pattern', 'script', 'api', 'function', 'variable', 'logic',
      'designed', 'created', 'developer', 'built this', 'made this',
      'meta', 'fourth wall', 'simulation', 'training', 'test',
      'genius engine', 'behavioral', 'tracking', 'telemetry',
      'watching me', 'analyzing', 'monitoring',
    ];
    let count = 0;
    for (const ind of indicators) {
      if (lower.includes(ind)) count++;
    }
    return count >= 2;
  }

  _detectSystemsThinking(input) {
    const lower = input.toLowerCase();
    const indicators = [
      'because', 'therefore', 'if.*then', 'connected', 'relationship',
      'depends on', 'affects', 'consequences', 'chain', 'cascade',
      'root cause', 'underlying', 'fundamental', 'infrastructure',
      'architecture', 'framework', 'ecosystem', 'network',
    ];
    let count = 0;
    for (const ind of indicators) {
      if (new RegExp(ind).test(lower)) count++;
    }
    return count >= 2;
  }

  _detectCreativity(input) {
    const lower = input.toLowerCase();
    const indicators = [
      'what if', 'imagine', 'instead', 'alternative', 'different way',
      'creative', 'unconventional', 'outside the box', 'reverse',
      'combine', 'merge', 'hybrid', 'remix', 'transform', 'invent',
    ];
    let count = 0;
    for (const ind of indicators) {
      if (lower.includes(ind)) count++;
    }
    return count >= 1;
  }

  _structuralComplexity(input) {
    let score = 0;
    if (input.includes(',')) score += 1;
    if (input.includes(';')) score += 2;
    if (input.includes(':')) score += 1;
    if (input.includes('?')) score += 1;
    if (input.includes('!')) score += 1;
    if (/\(.*\)/.test(input)) score += 2;
    if (/".*"/.test(input)) score += 1;
    if (input.length > 100) score += 2;
    if (input.split(/[.!?]/).length > 2) score += 2;
    return score;
  }

  _getChoiceDistribution(choices) {
    const dist = {};
    for (const c of choices) dist[c.choiceType] = (dist[c.choiceType] || 0) + 1;
    return dist;
  }

  _calcDivergenceIndex(choices) {
    if (choices.length < 2) return 0;
    const expectedDist = { smart: 0.6, academic: 0.25, aggressive: 0.15 };
    const actual = this._getChoiceDistribution(choices);
    const total = choices.length;
    let divergence = 0;
    for (const type of ['smart', 'academic', 'aggressive']) {
      const expected = expectedDist[type] || 0;
      const actualPct = (actual[type] || 0) / total;
      divergence += Math.abs(actualPct - expected);
    }
    return Math.round(divergence * 100) / 100;
  }

  _calcDeliberationScore() {
    const timings = this.metrics.timingDeltas;
    if (timings.length < 2) return 0;
    const avg = timings.reduce((a, b) => a + b, 0) / timings.length;
    return Math.min(1, avg / 10000);
  }

  _avgDeliberation() {
    const t = this.metrics.timingDeltas;
    if (t.length === 0) return 0;
    return Math.round(t.reduce((a, b) => a + b, 0) / t.length);
  }

  _avgEntropy(terminals) {
    if (terminals.length === 0) return 0;
    const entropies = terminals.map(t =>
      t.analysis ? t.analysis.entropy : this._shannonEntropy(t.input.split(''))
    );
    return Math.round((entropies.reduce((a, b) => a + b, 0) / entropies.length) * 100) / 100;
  }

  _avgLexicalDiversity(terminals) {
    if (terminals.length === 0) return 0;
    const divs = terminals.map(t =>
      t.analysis ? t.analysis.lexicalDiversity :
      this._lexicalDiversity(t.input.toLowerCase().split(/\s+/))
    );
    return Math.round((divs.reduce((a, b) => a + b, 0) / divs.length) * 100) / 100;
  }

  _calcExplorationScore(choices, terminals, sims) {
    let score = 0;
    const typesUsed = new Set(choices.map(c => c.choiceType));
    score += typesUsed.size * 15;
    score += terminals.length * 10;
    score += this.metrics.uniqueTerminalCommands.size * 5;
    score += sims.length * 5;
    if (this.flags.size > 0) score += this.flags.size * 30;
    return score;
  }

  _calcGritIndex() {
    if (!this._profile) return 0;
    const attempts = this._profile.attempts || [];
    if (attempts.length < 2) return 0;
    let shifts = 0;
    for (let i = 1; i < attempts.length; i++) {
      const prev = attempts[i - 1].choicePattern || '';
      const curr = attempts[i].choicePattern || '';
      if (prev !== curr) shifts++;
    }
    const retryRate = Math.min(1, attempts.length / 5);
    const adaptRate = shifts / (attempts.length - 1);
    const scores = attempts.map(a => a.trust || 0);
    const improvement = scores.length > 1
      ? (scores[scores.length - 1] - scores[0]) / Math.max(1, Math.abs(scores[0]) + 1)
      : 0;
    return Math.round((retryRate * 0.3 + adaptRate * 0.4 + Math.max(0, improvement) * 0.3) * 100) / 100;
  }

  // --- Persistence ---

  save() {
    const profile = this.getProfile();
    const data = {
      missionId: this.missionId,
      events: this.events,
      flags: [...this.flags],
      profile,
      savedAt: new Date().toISOString(),
    };

    try {
      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      existing.push(data);
      if (existing.length > 50) existing.splice(0, existing.length - 50);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
      this._updateProfile(profile);
    } catch (e) {}

    return data;
  }

  _loadProfile() {
    try {
      return JSON.parse(localStorage.getItem(PROFILE_KEY)) || { attempts: [], totalSessions: 0 };
    } catch {
      return { attempts: [], totalSessions: 0 };
    }
  }

  _updateProfile(sessionProfile) {
    const profile = this._loadProfile();
    profile.totalSessions++;
    profile.lastPlayed = new Date().toISOString();
    profile.attempts.push({
      missionId: this.missionId,
      trust: this.events.find(e => e.type === 'mission_complete')?.trust || 0,
      choicePattern: this.metrics.choicePatterns.join(','),
      flags: [...this.flags],
      explorationScore: sessionProfile.explorationScore,
      timestamp: Date.now(),
    });
    if (profile.attempts.length > 100) profile.attempts.splice(0, profile.attempts.length - 100);
    profile.totalFlags = new Set(profile.attempts.flatMap(a => a.flags || [])).size;
    profile.bestExploration = Math.max(profile.bestExploration || 0, sessionProfile.explorationScore);

    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    } catch (e) {}
  }
}

export default GeniusEngine;
